import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

const STORAGE_KEY = 'sanitization_filters';

export interface SharedFilters {
  plant: string | null;
  mainWorkCenters: string[] | null;
  functionalLocation: string | null;
}

export interface WorkOrderFilters {
  workOrderId: string | null;
  createdOn: string | null;
  originalDueDate: string | null;
  priority: string | null;
  description: string | null;
  duplicates: string | null;
  workOrderType: string | null;
  userStatus: string | null;
  functionalLocationId: string | null;
}

export interface WorkNotificationFilters {
  workNotificationId: string | null;
  createdOn: string | null;
  description: string | null;
  candidates: number | null;
  functionalLocationId: string | null;
}

export const SHARED_FILTER_FIELDS = {
  PLANT: 'SAP_Plant',
  MAIN_WORK_CENTER: 'SAP_MainWorkCenter',
  FUNCTIONAL_LOCATION: 'SAP_FunctionalLocationDescription',
} as const;

export const WORK_ORDER_FILTER_FIELDS = {
  WORK_ORDER_ID: 'SAP_WorkOrderId',
  PLANT: 'SAP_Plant',
  MAIN_WORK_CENTER: 'SAP_MainWorkCenter',
  FUNCTIONAL_LOCATION: 'SAP_FunctionalLocationDescription',
  FUNCTIONAL_LOCATION_ID: 'SAP_FunctionalLocation',
  CREATED_ON: 'SAP_CreatedOn',
  ORIGINAL_DUE_DATE: 'SAP_OriginalDueDate',
  PRIORITY: 'SAP_Priority',
  DESCRIPTION: 'SAP_Description',
  DUPLICATES: 'FilterForWorkOrdersWithDuplicates',
  WORK_ORDER_TYPE: 'SAP_WorkOrderType',
  USER_STATUS: 'UserStatus',
} as const;

export const WORK_NOTIFICATION_FILTER_FIELDS = {
  WORK_NOTIFICATION_ID: 'SAP_WorkNotificationId',
  PLANT: 'SAP_Plant',
  MAIN_WORK_CENTER: 'SAP_MainWorkCenter',
  FUNCTIONAL_LOCATION: 'SAP_FunctionalLocationDescription',
  FUNCTIONAL_LOCATION_ID: 'SAP_FunctionalLocation',
  CREATED_ON: 'SAP_CreatedOn',
  DESCRIPTION: 'SAP_Description',
  CANDIDATES: 'FilterWorkNotificationWithCandidates',
} as const;

const DEFAULT_SHARED: SharedFilters = {
  plant: null,
  mainWorkCenters: null,
  functionalLocation: null,
};
const DEFAULT_WORK_ORDER: WorkOrderFilters = {
  workOrderId: null,
  createdOn: null,
  originalDueDate: null,
  priority: null,
  description: null,
  duplicates: null,
  workOrderType: null,
  userStatus: null,
  functionalLocationId: null,
};
const DEFAULT_WORK_NOTIFICATION: WorkNotificationFilters = {
  workNotificationId: null,
  createdOn: null,
  description: null,
  candidates: null,
  functionalLocationId: null,
};

@Injectable({ providedIn: 'root' })
export class SanitizationFilterService {
  private _plant = new BehaviorSubject<string | null>(this.loadShared().plant);
  private _mainWorkCenters = new BehaviorSubject<string[] | null>(
    this.loadShared().mainWorkCenters,
  );
  private _functionalLocation = new BehaviorSubject<string | null>(
    this.loadShared().functionalLocation,
  );

  readonly plant$: Observable<string | null> = this._plant.asObservable();
  readonly mainWorkCenters$: Observable<string[] | null> =
    this._mainWorkCenters.asObservable();
  readonly functionalLocation$: Observable<string | null> =
    this._functionalLocation.asObservable();

  isClearing = false;
  private _clearAll$ = new Subject<void>();
  readonly clearAll$: Observable<void> = this._clearAll$.asObservable();

  private loadShared(): SharedFilters {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_SHARED };
      const parsed = JSON.parse(raw).shared ?? {};
      const merged = { ...DEFAULT_SHARED, ...parsed };
      // Migrate: discard legacy single-string mainWorkCenter value
      if (!Array.isArray(merged.mainWorkCenters)) merged.mainWorkCenters = null;
      return merged;
    } catch {
      return { ...DEFAULT_SHARED };
    }
  }

  private saveShared(shared: SharedFilters): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : {};
      data.shared = shared;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  setPlant(value: string | null): void {
    this.saveShared({ ...this.loadShared(), plant: value });
    this._plant.next(value);
  }

  setMainWorkCenters(value: string[] | null): void {
    this.saveShared({ ...this.loadShared(), mainWorkCenters: value });
    this._mainWorkCenters.next(value);
  }

  setFunctionalLocation(value: string | null): void {
    this.saveShared({ ...this.loadShared(), functionalLocation: value });
    this._functionalLocation.next(value);
  }

  getShared(): SharedFilters {
    return this.loadShared();
  }

  persistShared(partial: Partial<SharedFilters>): void {
    this.saveShared({ ...this.loadShared(), ...partial });
  }

  getWorkOrder(): WorkOrderFilters {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_WORK_ORDER };
      return { ...DEFAULT_WORK_ORDER, ...JSON.parse(raw).workOrder };
    } catch {
      return { ...DEFAULT_WORK_ORDER };
    }
  }

  setWorkOrder(partial: Partial<WorkOrderFilters>): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : {};
      data.workOrder = { ...DEFAULT_WORK_ORDER, ...data.workOrder, ...partial };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  getWorkNotification(): WorkNotificationFilters {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_WORK_NOTIFICATION };
      return {
        ...DEFAULT_WORK_NOTIFICATION,
        ...JSON.parse(raw).workNotification,
      };
    } catch {
      return { ...DEFAULT_WORK_NOTIFICATION };
    }
  }

  setWorkNotification(partial: Partial<WorkNotificationFilters>): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : {};
      data.workNotification = {
        ...DEFAULT_WORK_NOTIFICATION,
        ...data.workNotification,
        ...partial,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  clearWorkOrder(): void {
    this.setPlant(null);
    this.setMainWorkCenters(null);
    this.setFunctionalLocation(null);
    this.setWorkOrder({ ...DEFAULT_WORK_ORDER });
  }

  clearWorkNotification(): void {
    this.setPlant(null);
    this.setMainWorkCenters(null);
    this.setFunctionalLocation(null);
    this.setWorkNotification({ ...DEFAULT_WORK_NOTIFICATION });
  }

  clearAll(): void {
    this.isClearing = true;
    this.saveShared({ ...DEFAULT_SHARED });
    this._plant.next(null);
    this._mainWorkCenters.next(null);
    this._functionalLocation.next(null);
    this.setWorkOrder({ ...DEFAULT_WORK_ORDER });
    this.setWorkNotification({ ...DEFAULT_WORK_NOTIFICATION });
    this._clearAll$.next();
    this.isClearing = false;
  }

  buildSharedTableFilters(): Record<string, { value: any; matchMode: string }> {
    const shared = this.loadShared();
    const filters: Record<string, { value: any; matchMode: string }> = {};
    if (shared.plant)
      filters[SHARED_FILTER_FIELDS.PLANT] = {
        value: shared.plant,
        matchMode: 'contains',
      };
    if (shared.mainWorkCenters?.length)
      filters[SHARED_FILTER_FIELDS.MAIN_WORK_CENTER] = {
        value: shared.mainWorkCenters,
        matchMode: 'in',
      };
    if (shared.functionalLocation)
      filters[SHARED_FILTER_FIELDS.FUNCTIONAL_LOCATION] = {
        value: shared.functionalLocation,
        matchMode: 'contains',
      };
    return filters;
  }
}
